import * as echarts from 'echarts/core';
import { BarChart, GaugeChart, LineChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { provideEchartsCore } from 'ngx-echarts';
import { ThemeEnum } from '../theme/models/enums/theme.enum';

echarts.use([
  BarChart,
  GaugeChart,
  LineChart,
  PieChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
]);

/**
 * Cores dos gráficos do dashboard por tema. Os componentes leem a paleta do tema atual e reconstroem as opções
 * quando o tema muda. Os valores seguem os tokens de src/styles/theme/_tokens.scss
 */
export interface DashboardChartPalette {
  text: string;
  subtext: string;
  axis: string;
  splitLine: string;
  tooltipBackground: string;
  tooltipBorder: string;
  track: string;
  progressTrack: string;
  progress: string;
  dottedRing: string;
}

export const DASHBOARD_CHART_PALETTES: Record<ThemeEnum, DashboardChartPalette> = {
  [ThemeEnum.DARK]: {
    text: '#ffffff',
    subtext: '#a1a2a7',
    axis: '#545661',
    splitLine: '#3a3c44',
    tooltipBackground: '#2e303b',
    tooltipBorder: '#545661',
    track: '#2e303b',
    progressTrack: '#1f2128',
    progress: '#4a3aff',
    dottedRing: 'rgba(255, 255, 255, 0.7)',
  },
  [ThemeEnum.LIGHT]: {
    text: '#252731',
    subtext: '#626272',
    axis: '#ccccda',
    splitLine: '#d4d4e0',
    tooltipBackground: '#f3f3f8',
    tooltipBorder: '#ccccda',
    track: '#ccccda',
    progressTrack: '#ccccda',
    progress: '#4a3aff',
    dottedRing: 'rgba(37, 39, 49, 0.35)',
  },
};

const buildDashboardTheme = (palette: DashboardChartPalette): object => {
  const axis = {
    axisLine: { lineStyle: { color: palette.axis } },
    axisTick: { lineStyle: { color: palette.axis } },
    axisLabel: { color: palette.subtext },
    splitLine: { lineStyle: { color: palette.splitLine } },
  };

  return {
    color: ['#4a3aff', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#9948ff', '#44c18a'],
    backgroundColor: 'transparent',
    textStyle: {
      color: palette.subtext,
      fontFamily: 'Montserrat, sans-serif',
    },
    title: {
      textStyle: { color: palette.text },
      subtextStyle: { color: palette.subtext },
    },
    legend: {
      textStyle: { color: palette.subtext },
    },
    tooltip: {
      backgroundColor: palette.tooltipBackground,
      borderColor: palette.tooltipBorder,
      textStyle: { color: palette.text },
    },
    axisPointer: {
      lineStyle: { color: palette.axis },
      crossStyle: { color: palette.axis },
    },
    categoryAxis: axis,
    valueAxis: axis,
  };
};

export const ECHARTS_THEMES: Record<ThemeEnum, string> = {
  [ThemeEnum.DARK]: 'liga-dark',
  [ThemeEnum.LIGHT]: 'liga-light',
};

echarts.registerTheme(ECHARTS_THEMES[ThemeEnum.DARK], buildDashboardTheme(DASHBOARD_CHART_PALETTES[ThemeEnum.DARK]));
echarts.registerTheme(ECHARTS_THEMES[ThemeEnum.LIGHT], buildDashboardTheme(DASHBOARD_CHART_PALETTES[ThemeEnum.LIGHT]));

export const ECHARTS_THEME = ECHARTS_THEMES[ThemeEnum.DARK];

export const provideDashboardEcharts = (): ReturnType<typeof provideEchartsCore> =>
  provideEchartsCore({ echarts });
